/**
 * Back to the top of a long phone list — `docs/phone-list-loading.md` §4.
 *
 * A list that loads as you scroll has no bottom to reach and therefore no
 * natural way back, so after two screens this fades in. It is a 40px tinted
 * circle in the contact-avatar idiom — the same shape the rows themselves
 * use — and not a dark pill: a black capsule floating over a warm off-white
 * list is the one element on the screen that belongs to a different product.
 *
 * It floats and never pushes content, and it sits above whatever the bottom
 * edge already holds: the home indicator on a standalone list, the tab bar on
 * a record page.
 *
 * **It does not listen to `window`.** The app frame's `<main>` is the scroll
 * box — the document itself never moves — so a button watching `window.scrollY`
 * would sit at zero forever and never appear. It finds the box it is in
 * instead, from an anchor it leaves in the tree.
 */
import { useEffect, useRef, useState } from 'react'
import { cn } from '../lib/cn.js'
import { IconArrowUp } from '../frame/icons.js'

/** The nearest ancestor that actually scrolls, or the window. */
function scrollBoxOf(node: HTMLElement | null): HTMLElement | Window {
  let at = node?.parentElement ?? null
  while (at) {
    const overflow = getComputedStyle(at).overflowY
    if ((overflow === 'auto' || overflow === 'scroll') && at.scrollHeight > at.clientHeight) {
      return at
    }
    at = at.parentElement
  }
  return window
}

/*
 * `box === window`, not `box instanceof Window`.
 *
 * Under jsdom the global `Window` and the realm the window object came from
 * are not the same one, so the `instanceof` is false for the actual window
 * and both reads came back `undefined` — a button that could never appear,
 * and a test that could never say why.
 */
const isWindow = (box: HTMLElement | Window): box is Window => box === window

const offsetOf = (box: HTMLElement | Window) =>
  (isWindow(box) ? box.scrollY : box.scrollTop)

const heightOf = (box: HTMLElement | Window) =>
  (isWindow(box) ? box.innerHeight : box.clientHeight)

export function ScrollTopButton({ bottom = 12 }: {
  /** Pixels above the safe area. A record page's tab bar is 58 of them. */
  bottom?: number
}) {
  const anchor = useRef<HTMLSpanElement>(null)
  const box = useRef<HTMLElement | Window>(window)
  const [shown, setShown] = useState(false)

  useEffect(() => {
    const found = scrollBoxOf(anchor.current)
    box.current = found

    const onScroll = () => setShown(offsetOf(found) > 2 * heightOf(found))
    onScroll()
    found.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      found.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
    /* The list grows as it loads, so what scrolls can change under us — an
       element that was too short to scroll on the first page is the scroll
       box by the third. Re-resolved whenever this re-renders into view. */
  }, [shown])

  return (
    <>
      <span ref={anchor} aria-hidden className="hidden" />
      {shown && (
        <button
          type="button"
          aria-label="Back to top"
          onClick={() => {
            const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
            /* Somebody who asked the system for less motion asked for less
               motion, not for a different button. */
            box.current.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' })
          }}
          style={{ bottom: `calc(env(safe-area-inset-bottom) + ${bottom}px)` }}
          className={cn('fixed right-4 z-30 flex h-10 w-10 items-center justify-center',
            'rounded-full bg-accent-soft text-accent-dark ring-1 ring-accent-line',
            'shadow-[0_2px_8px_rgba(20,20,10,.12)] transition-opacity')}
        >
          <IconArrowUp size={18} />
        </button>
      )}
    </>
  )
}
